import React, { useEffect, useRef } from 'react';
import { 
  Paper, 
  Typography, 
  Button, 
  makeStyles, 
  CircularProgress,
  Chip 
} from '@material-ui/core';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import usePaymentConfiguration from '../../hooks/usePaymentConfiguration';
import { useLocation } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: '25px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontSize: '20px',
    fontWeight: '700',
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: '10px',
    color: theme.palette.primary.main,
  },
  content: {
    flex: 1,
    marginBottom: '15px',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  },
  statusIcon: {
    marginRight: '8px',
    fontSize: '20px',
  },
  configuredIcon: {
    color: '#4caf50',
  },
  notConfiguredIcon: {
    color: '#f44336',
  },
  publicKey: {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    marginTop: '5px',
    fontFamily: 'monospace',
  },
  button: {
    marginTop: 'auto',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60px',
  },
  chip: {
    marginTop: '10px',
  },
}));

const PaymentConfigurationCard = () => {
  const classes = useStyles();
  const location = useLocation();
  const processingRef = useRef(false);
  const {
    loading,
    configuration,
    isConfigured,
    getAuthorizationUrl,
    linkPayment,
    unlinkPayment,
    getPartialPublicKey,
  } = usePaymentConfiguration();

  useEffect(() => {
    // Verificar si venimos del callback de Mercado Pago
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (code && state && state.startsWith('fasterstore-') && !processingRef.current) {
      processingRef.current = true;
      linkPayment(code, state).finally(() => {
        processingRef.current = false;
      });
      // Limpiar los parámetros de la URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.search, linkPayment]);

  if (loading) {
    return (
      <Paper className={classes.paper}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} />
        </div>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      <Typography className={classes.title}>
        <CreditCardIcon className={classes.icon} />
        Configuración de pagos con tarjeta (Mercado Pago)
      </Typography>
      
      <div className={classes.content}>
        <div className={classes.status}>
          {isConfigured ? (
            <>
              <CheckCircleIcon className={`${classes.statusIcon} ${classes.configuredIcon}`} />
              <Typography>Cuenta de Mercado Pago conectada</Typography>
            </>
          ) : (
            <>
              <ErrorIcon className={`${classes.statusIcon} ${classes.notConfiguredIcon}`} />
              <Typography>No configurado</Typography>
            </>
          )}
        </div>
        
        {isConfigured && configuration && (
          <>
            <Typography className={classes.publicKey}>
              Public Key: {getPartialPublicKey()}
            </Typography>
            <Chip 
              label="Configurado"
              color="primary"
              size="small"
              className={classes.chip}
            />
          </>
        )}
        
        {!isConfigured && (
          <Typography variant="body2" color="textSecondary">
            Configurá tu cuenta de Mercado Pago para recibir tus ventas online directo a tu local. 
            <br />* Disponible según tu plan actual.
          </Typography>
        )}
      </div>

      {isConfigured ? (
        <Button
          variant="outlined"
          color="secondary"
          onClick={unlinkPayment}
          className={classes.button}
          disabled={loading}
        >
          Quitar configuración
        </Button>
      ) : (
        <Button
          variant="contained"
          color="primary"
          onClick={getAuthorizationUrl}
          className={classes.button}
          disabled={loading}
        >
          Configurar ahora
        </Button>
      )}
    </Paper>
  );
};

export default PaymentConfigurationCard; 